import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, Send, Search, BadgeCheck, Download, FileText, FileSpreadsheet, UserCog } from "lucide-react";
import PhoneInput from "../../components/PhoneInput";
import { fullPhone, checkPhone } from "../../lib/validation";
import { downloadFile } from "../../lib/download";

const FREQ = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
];

const EMPTY_STUDENT = {
    name: "", contact: "", email: "", centre_id: "", coach_id: "", sport: "",
    time_slot: "", fees: 0, fees_frequency: "monthly", last_paid_on: "", notes: "", active: true,
    dob: "", guardian_name: "",   // Iter 40 — mandatory
};

const EMPTY_COACH = {
    name: "", centre_id: "", sport: "", phone: "", email: "", active: true,
};

const TABS = [
    { key: "students", label: "Students", icon: GraduationCap },
    { key: "coaches", label: "Coaches", icon: UserCog },
];

export default function AdminAcademy() {
    const [tab, setTab] = useState("students");
    const [students, setStudents] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [centres, setCentres] = useState([]);

    // Student form/filter state
    const [centreFilter, setCentreFilter] = useState("");
    const [q, setQ] = useState("");
    const [studentForm, setStudentForm] = useState(EMPTY_STUDENT);
    const [editingStudent, setEditingStudent] = useState(null);
    const [studentOpen, setStudentOpen] = useState(false);

    // Coach form state
    const [coachForm, setCoachForm] = useState(EMPTY_COACH);
    const [editingCoach, setEditingCoach] = useState(null);
    const [coachOpen, setCoachOpen] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const fetchAll = async () => {
        const params = {};
        if (centreFilter) params.centre_id = centreFilter;
        if (q) params.q = q;
        const [s, co, c] = await Promise.all([
            api.get("/admin/students", { params }),
            api.get("/admin/coaches"),
            api.get("/centres"),
        ]);
        setStudents(s.data);
        setCoaches(co.data);
        setCentres(c.data);
    };
    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [centreFilter, q]);

    // ----- Coach actions -----
    const openCreateCoach = () => { setEditingCoach(null); setCoachForm({ ...EMPTY_COACH, centre_id: centres[0]?.id || "" }); setCoachOpen(true); };
    const openEditCoach = (c) => {
        setEditingCoach(c);
        setCoachForm({
            name: c.name || "",
            centre_id: c.centre_id || "",
            sport: c.sport || "",
            phone: (c.phone || "").replace(/^\+91/, ""),
            email: c.email || "",
            active: c.active !== false,
        });
        setCoachOpen(true);
    };
    const submitCoach = async (e) => {
        e.preventDefault();
        if (!coachForm.centre_id) { toast.error("Please pick a centre"); return; }
        if (coachForm.phone && !checkPhone(coachForm.phone).ok) { toast.error("Invalid phone"); return; }
        setSubmitting(true);
        try {
            const payload = {
                ...coachForm,
                phone: coachForm.phone ? fullPhone(coachForm.phone) : null,
                email: coachForm.email || null,
            };
            if (editingCoach) {
                await api.put(`/admin/coaches/${editingCoach.id}`, payload);
                toast.success("Coach updated");
            } else {
                await api.post("/admin/coaches", payload);
                toast.success("Coach added");
            }
            setCoachOpen(false);
            setEditingCoach(null);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };
    const removeCoach = async (c) => {
        if (!confirm(`Delete coach ${c.name}?`)) return;
        try {
            await api.delete(`/admin/coaches/${c.id}`);
            toast.success("Coach removed");
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    // ----- Student actions -----
    const openCreateStudent = () => { setEditingStudent(null); setStudentForm(EMPTY_STUDENT); setStudentOpen(true); };
    const openEditStudent = (s) => {
        setEditingStudent(s);
        setStudentForm({
            name: s.name || "",
            contact: (s.contact || "").replace(/^\+91/, ""),
            email: s.email || "",
            centre_id: s.centre_id || "",
            coach_id: s.coach_id || "",
            sport: s.sport || "",
            time_slot: s.time_slot || "",
            fees: s.fees || 0,
            fees_frequency: s.fees_frequency || "monthly",
            last_paid_on: s.last_paid_on || "",
            notes: s.notes || "",
            active: s.active !== false,
            dob: s.dob || "",
            guardian_name: s.guardian_name || "",   // Iter 40
        });
        setStudentOpen(true);
    };
    const submitStudent = async (e) => {
        e.preventDefault();
        if (!checkPhone(studentForm.contact).ok) { toast.error("Invalid contact number"); return; }
        if (!studentForm.coach_id) { toast.error("Select a coach. Add a coach first if none exist."); return; }
        setSubmitting(true);
        try {
            const payload = {
                ...studentForm,
                contact: fullPhone(studentForm.contact),
                fees: parseInt(studentForm.fees) || 0,
                last_paid_on: studentForm.last_paid_on || null,
                email: studentForm.email || null,
            };
            if (editingStudent) {
                await api.put(`/admin/students/${editingStudent.id}`, payload);
                toast.success("Student updated");
            } else {
                await api.post("/admin/students", payload);
                toast.success("Student added");
            }
            setStudentOpen(false);
            setEditingStudent(null);
            setStudentForm(EMPTY_STUDENT);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };
    const removeStudent = async (id) => {
        if (!confirm("Delete this student record?")) return;
        await api.delete(`/admin/students/${id}`);
        toast.success("Removed");
        fetchAll();
    };
    const sendReminder = async (s) => {
        try {
            const { data } = await api.post(`/admin/students/${s.id}/reminder`);
            toast.success(`Reminder sent (${data.status}) to ${s.contact}`);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };
    const markPaid = async (s) => {
        const today = new Date().toISOString().slice(0, 10);
        if (!confirm(`Mark ${s.name}'s ${s.fees_frequency} fee as paid today (${today})?`)) return;
        await api.post(`/admin/students/${s.id}/mark-paid`, { paid_on: today });
        toast.success("Marked as paid");
        fetchAll();
    };

    // ----- Exports -----
    const exportFile = async (format) => {
        try {
            const ext = format === "csv" ? "csv" : (format === "xlsx" ? "xlsx" : "pdf");
            const { data } = await api.get(`/admin/students/export.${ext}`, { responseType: "blob" });
            downloadFile(data, `academy_students.${ext}`);
        } catch (e) {
            toast.error(`${format.toUpperCase()} export failed`);
        }
    };

    const centreName = (id) => centres.find(c => c.id === id)?.name || "—";

    // Coaches filtered to selected centre when student form open
    const coachesForCentre = useMemo(() => {
        const cid = studentForm.centre_id;
        if (!cid) return coaches;
        return coaches.filter(c => c.centre_id === cid && c.active !== false);
    }, [coaches, studentForm.centre_id]);

    const counts = useMemo(() => ({
        total: students.length,
        active: students.filter(s => s.active !== false).length,
        due: students.filter(s => s.next_due_on && s.next_due_on <= new Date().toISOString().slice(0, 10)).length,
        coaches: coaches.length,
    }), [students, coaches]);

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <GraduationCap className="w-5 h-5 text-[#00FF66]" />
                            <h1 className="font-display text-3xl font-bold uppercase text-white">Academy</h1>
                        </div>
                        <p className="text-slate-400 text-sm mt-1">Add a coach + centre first, then add students under that coach. Export reports as CSV / Excel / PDF.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => exportFile("csv")} data-testid="export-csv-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-slate-900 border border-slate-700 text-slate-200 font-bold uppercase tracking-widest text-xs rounded hover:border-[#00FF66]/50">
                            <Download className="w-4 h-4" /> CSV
                        </button>
                        <button onClick={() => exportFile("xlsx")} data-testid="export-xlsx-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-slate-900 border border-slate-700 text-slate-200 font-bold uppercase tracking-widest text-xs rounded hover:border-[#00FF66]/50">
                            <FileSpreadsheet className="w-4 h-4" /> Excel
                        </button>
                        <button onClick={() => exportFile("pdf")} data-testid="export-pdf-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-slate-900 border border-slate-700 text-slate-200 font-bold uppercase tracking-widest text-xs rounded hover:border-[#00FF66]/50">
                            <FileText className="w-4 h-4" /> PDF
                        </button>
                        {tab === "students" ? (
                            <button onClick={openCreateStudent} data-testid="new-student-btn"
                                className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                                <Plus className="w-4 h-4" /> New Student
                            </button>
                        ) : (
                            <button onClick={openCreateCoach} data-testid="new-coach-btn"
                                className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                                <Plus className="w-4 h-4" /> New Coach
                            </button>
                        )}
                    </div>
                </div>

                {/* KPI */}
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
                    <Stat label="Coaches" value={counts.coaches} />
                    <Stat label="Students" value={counts.total} />
                    <Stat label="Active" value={counts.active} />
                    <Stat label="Due Now" value={counts.due} amber={counts.due > 0} />
                </div>

                {/* Tabs */}
                <div className="mt-6 flex gap-2 border-b border-slate-800">
                    {TABS.map(t => {
                        const I = t.icon;
                        return (
                            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                                className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-widest uppercase border-b-2 -mb-px transition ${
                                    tab === t.key ? "text-[#00FF66] border-[#00FF66]" : "text-slate-400 border-transparent hover:text-white"
                                }`}>
                                <I className="w-3.5 h-3.5" /> {t.label}
                            </button>
                        );
                    })}
                </div>

                {tab === "students" && (
                    <>
                        <div className="mt-5 flex gap-3 flex-wrap">
                            <div className="relative flex-1 min-w-[240px]">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / coach"
                                    data-testid="academy-search"
                                    className="w-full h-10 pl-9 pr-3 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>
                            <select value={centreFilter} onChange={(e) => setCentreFilter(e.target.value)} data-testid="academy-centre-filter"
                                className="h-10 px-3 bg-slate-900 border border-slate-700 rounded text-white text-sm">
                                <option value="">All Centres</option>
                                {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>

                        <div className="mt-5 bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto" data-testid="academy-students-table">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-950/60 border-b border-slate-800 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
                                    <tr>
                                        <th className="text-left px-4 py-3">Student</th>
                                        <th className="text-left px-4 py-3">Centre · Coach</th>
                                        <th className="text-left px-4 py-3">Schedule</th>
                                        <th className="text-right px-4 py-3">Fees</th>
                                        <th className="text-left px-4 py-3">Last Paid · Next Due</th>
                                        <th className="text-right px-4 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">No students yet</td></tr>}
                                    {students.map(s => {
                                        const overdue = s.next_due_on && s.next_due_on <= new Date().toISOString().slice(0, 10);
                                        return (
                                            <tr key={s.id} className="border-b border-slate-800/70" data-testid={`student-${s.id}`}>
                                                <td className="px-4 py-3">
                                                    <div className="font-bold text-white">{s.name}</div>
                                                    <div className="text-xs text-slate-400">{s.contact}{s.email ? ` · ${s.email}` : ""}</div>
                                                    {(s.dob || s.guardian_name) && (
                                                        <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">
                                                            {s.guardian_name ? <span data-testid={`student-guardian-${s.id}`}>Guardian: <span className="text-slate-300">{s.guardian_name}</span></span> : null}
                                                            {s.dob && s.guardian_name ? <span className="mx-1.5 text-slate-700">·</span> : null}
                                                            {s.dob ? <span data-testid={`student-dob-${s.id}`}>DOB: <span className="text-slate-300">{s.dob}</span></span> : null}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs">
                                                    <div className="text-slate-300">{centreName(s.centre_id)}</div>
                                                    <div className="text-slate-500">{s.coach_name ? `Coach ${s.coach_name}` : "—"} · {s.sport || "—"}</div>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-300">{s.time_slot || "—"}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="font-bold text-white">₹{(s.fees || 0).toLocaleString("en-IN")}</div>
                                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.fees_frequency}</div>
                                                </td>
                                                <td className="px-4 py-3 text-xs">
                                                    <div className="text-slate-300">{s.last_paid_on || "Not paid"}</div>
                                                    <div className={overdue ? "text-amber-400 font-bold" : "text-slate-500"}>
                                                        {s.next_due_on ? `Next: ${s.next_due_on}` : "—"}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="inline-flex items-center gap-1.5">
                                                        <button onClick={() => sendReminder(s)} data-testid={`remind-${s.id}`}
                                                            title="Send WhatsApp reminder"
                                                            className="px-2 h-8 rounded border border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10 text-[10px] font-bold uppercase tracking-widest">
                                                            <Send className="w-3 h-3 inline mr-1" /> Remind
                                                        </button>
                                                        <button onClick={() => markPaid(s)} data-testid={`paid-${s.id}`}
                                                            title="Mark as paid today"
                                                            className="px-2 h-8 rounded border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 text-[10px] font-bold uppercase tracking-widest">
                                                            <BadgeCheck className="w-3 h-3 inline mr-1" /> Paid
                                                        </button>
                                                        <button onClick={() => openEditStudent(s)} data-testid={`edit-student-${s.id}`}
                                                            className="text-slate-400 hover:text-sky-400 px-1">
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => removeStudent(s.id)} data-testid={`del-student-${s.id}`}
                                                            className="text-slate-500 hover:text-red-400 px-1">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {tab === "coaches" && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="coaches-grid">
                        {coaches.length === 0 && (
                            <div className="md:col-span-2 text-center text-slate-500 py-10 bg-slate-900 border border-slate-800 rounded-lg">
                                No coaches yet. Click <span className="text-[#00FF66] font-bold">+ New Coach</span> to add one.
                            </div>
                        )}
                        {coaches.map(c => (
                            <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex items-center gap-4" data-testid={`coach-${c.id}`}>
                                <div className="w-12 h-12 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center flex-shrink-0">
                                    <UserCog className="w-5 h-5 text-[#00FF66]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-bold text-white truncate">{c.name}</div>
                                        {c.active === false && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">Inactive</span>}
                                    </div>
                                    <div className="text-xs text-slate-400 truncate">{centreName(c.centre_id)}{c.sport ? ` · ${c.sport}` : ""}</div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">{c.phone || "—"} · {c.student_count || 0} student{(c.student_count || 0) === 1 ? "" : "s"}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => openEditCoach(c)} data-testid={`edit-coach-${c.id}`} className="p-1 text-slate-400 hover:text-sky-400">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => removeCoach(c)} data-testid={`del-coach-${c.id}`} className="p-1 text-slate-500 hover:text-red-400">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Coach Modal */}
                {coachOpen && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
                        <form onSubmit={submitCoach} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 my-6" data-testid="coach-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editingCoach ? "Edit Coach" : "New Coach"}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Name" testid="coach-name">
                                    <input value={coachForm.name} onChange={(e) => setCoachForm({ ...coachForm, name: e.target.value })} required minLength={2}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Centre" testid="coach-centre">
                                    <select value={coachForm.centre_id} onChange={(e) => setCoachForm({ ...coachForm, centre_id: e.target.value })} required
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                        <option value="">— Select —</option>
                                        {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </Field>
                                <Field label="Sport" testid="coach-sport">
                                    <input value={coachForm.sport} onChange={(e) => setCoachForm({ ...coachForm, sport: e.target.value })}
                                        placeholder="Football / Box Cricket"
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Phone (optional)">
                                    <PhoneInput value={coachForm.phone} onChange={(v) => setCoachForm({ ...coachForm, phone: v })} testid="coach-phone" />
                                </Field>
                                <Field label="Email (optional)" testid="coach-email">
                                    <input type="email" value={coachForm.email} onChange={(e) => setCoachForm({ ...coachForm, email: e.target.value })}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
                                <input type="checkbox" checked={coachForm.active} onChange={(e) => setCoachForm({ ...coachForm, active: e.target.checked })} className="w-4 h-4 accent-[#00FF66]" />
                                Active
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setCoachOpen(false); setEditingCoach(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" disabled={submitting} data-testid="save-coach-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">
                                    {submitting ? "Saving..." : (editingCoach ? "Update" : "Create")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Student Modal */}
                {studentOpen && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
                        <form onSubmit={submitStudent} className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 my-6" data-testid="student-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editingStudent ? "Edit Student" : "New Student"}</h3>
                            <div className="text-[11px] text-slate-500 mt-0.5">Pick the centre and coach first — the student will be linked to that coach.</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Centre" testid="student-centre">
                                    <select value={studentForm.centre_id}
                                        onChange={(e) => setStudentForm({ ...studentForm, centre_id: e.target.value, coach_id: "" })}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                        <option value="">— Select Centre —</option>
                                        {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </Field>
                                <Field label="Coach" testid="student-coach-select">
                                    <select value={studentForm.coach_id}
                                        onChange={(e) => setStudentForm({ ...studentForm, coach_id: e.target.value })}
                                        required
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                        <option value="">— Select Coach —</option>
                                        {coachesForCentre.map(c => <option key={c.id} value={c.id}>{c.name}{c.sport ? ` · ${c.sport}` : ""}</option>)}
                                    </select>
                                    {coachesForCentre.length === 0 && (
                                        <div className="text-[11px] text-amber-400 mt-1">No coaches at this centre. Add a coach first.</div>
                                    )}
                                </Field>
                                <Field label="Name" testid="student-name">
                                    <input value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} required minLength={2}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Contact (phone)">
                                    <PhoneInput value={studentForm.contact} onChange={(v) => setStudentForm({ ...studentForm, contact: v })} testid="student-contact" />
                                </Field>
                                {/* Iter 40 — mandatory DOB + Guardian Name */}
                                <Field label="Date of Birth" testid="student-dob">
                                    <input type="date" value={studentForm.dob}
                                        onChange={(e) => setStudentForm({ ...studentForm, dob: e.target.value })}
                                        required
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Guardian Name" testid="student-guardian">
                                    <input value={studentForm.guardian_name}
                                        onChange={(e) => setStudentForm({ ...studentForm, guardian_name: e.target.value })}
                                        required minLength={2}
                                        placeholder="Parent / guardian full name"
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Email (optional)" testid="student-email">
                                    <input type="email" value={studentForm.email} onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Sport" testid="student-sport">
                                    <input value={studentForm.sport} onChange={(e) => setStudentForm({ ...studentForm, sport: e.target.value })}
                                        placeholder="Football / Box Cricket"
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Time Slot" testid="student-timeslot">
                                    <input value={studentForm.time_slot} onChange={(e) => setStudentForm({ ...studentForm, time_slot: e.target.value })}
                                        placeholder="Mon/Wed/Fri 06:00–07:00"
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Fees (₹)" testid="student-fees">
                                    <input type="number" value={studentForm.fees} onChange={(e) => setStudentForm({ ...studentForm, fees: e.target.value })}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                                <Field label="Frequency" testid="student-frequency">
                                    <select value={studentForm.fees_frequency} onChange={(e) => setStudentForm({ ...studentForm, fees_frequency: e.target.value })}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                        {FREQ.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Last Paid On" testid="student-last-paid">
                                    <input type="date" value={studentForm.last_paid_on} onChange={(e) => setStudentForm({ ...studentForm, last_paid_on: e.target.value })}
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </Field>
                            </div>
                            <Field label="Notes" testid="student-notes">
                                <input value={studentForm.notes} onChange={(e) => setStudentForm({ ...studentForm, notes: e.target.value })}
                                    className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                            </Field>
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
                                <input type="checkbox" checked={studentForm.active} onChange={(e) => setStudentForm({ ...studentForm, active: e.target.checked })} className="w-4 h-4 accent-[#00FF66]" />
                                Active
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setStudentOpen(false); setEditingStudent(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" disabled={submitting} data-testid="save-student-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">
                                    {submitting ? "Saving..." : (editingStudent ? "Update" : "Create")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}

function Stat({ label, value, amber }) {
    return (
        <div className={`rounded-lg border p-3 text-center ${amber ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-900 border-slate-800"}`}>
            <div className={`font-display text-2xl font-bold ${amber ? "text-amber-400" : "text-white"}`}>{value}</div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mt-0.5">{label}</div>
        </div>
    );
}
